import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    ElementRef,
    forwardRef,
    inject,
    input,
    model,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ShiftSelectable } from "../s25-shift-select/s25.shift.selectable.abstract";
import { FormValueControl } from "@angular/forms/signals";
import { AngBridgeUtil } from "../../util/ang.bridge.util";

@TypeManagerDecorator("s25-ng-checkbox")
@Component({
    selector: "s25-ng-checkbox",
    template: `
        <div
            class="wrapper"
            [class]="containerClass()"
            [class.rowCheckboxLabelContainer]="labelClass() === 'rowCheckBoxLabel'"
            [class.c-displayInlineBlock]="labelClass() === 'inline'"
            (click)="passiveCheck($event)"
            (keydown.enter)="passiveCheck($event)"
            (keydown.space)="passiveCheck($event)"
        >
            <input
                type="checkbox"
                [id]="idString()"
                [ngModel]="value()"
                [ngClass]="{ checked: value(), 'c-label--input': true }"
                (ngModelChange)="setValue($event)"
                [disabled]="disabled()"
                [attr.aria-label]="ariaLabel()"
            />
            <label [for]="idString()" class="c-label--checkbox" [class]="labelClass()">
                @if (!noLabel()) {
                    <span [style.margin-left]="'0.5em'">
                        <ng-content></ng-content>
                    </span>
                }
            </label>
            <s25-ng-fading-success-msg [(message)]="successMessage" class="checkbox-success-msg" />
        </div>
    `,
    styles: `
        s25-ng-checkbox .wrapper {
            vertical-align: middle;
            margin: 0.25em 0;
            position: relative;
        }

        s25-ng-checkbox .c-label--checkbox {
            display: flex !important;
            margin: 0;
        }

        s25-ng-checkbox .c-label--checkbox::before {
            margin-right: 0;
        }

        s25-ng-checkbox input[disabled] + label {
            opacity: 0.75;
        }

        s25-ng-checkbox input[disabled] + label,
        s25-ng-checkbox input[disabled] + label:hover {
            cursor: not-allowed;
        }

        s25-ng-checkbox input[disabled] + label::before,
        s25-ng-checkbox input[disabled] + label:hover::before {
            border-style: dashed;
        }

        .nm-party--on s25-ng-checkbox input[disabled] + label:hover::before {
            border-color: #3f627f !important;
        }

        s25-ng-checkbox .checkbox-success-msg {
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
        }

        s25-ng-checkbox .checkbox-success-msg .success-msg {
            position: absolute;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--color--surface-bg);
        }

        s25-ng-checkbox:has(.checkbox-success-msg.has-message) .c-label--checkbox::before,
        s25-ng-checkbox:has(.checkbox-success-msg.has-message) .c-label--checkbox::after {
            visibility: hidden;
        }
    `,
    providers: [
        {
            provide: ShiftSelectable,
            useExisting: forwardRef(() => S25CheckboxComponent),
        },
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25CheckboxComponent implements ShiftSelectable, FormValueControl<boolean> {
    readonly value = model<boolean>(false);
    readonly successMessage = model<string>("");
    readonly onChange = input<((value: { data: boolean }) => void) | undefined>(undefined);
    readonly blockChange = input<(() => boolean | Promise<boolean>) | undefined>(undefined);
    readonly disabled = input(false, { transform: (bool) => !!bool });
    readonly containerClass = input<string>("");
    readonly labelId = input<string | undefined>(undefined);
    readonly labelClass = input<string>("inline"); //"rowCheckBoxLabel", "inline"
    readonly ariaLabel = input<string | undefined>(undefined);
    readonly noLabel = input<boolean>(false); // Hides label when true
    readonly passive = input<boolean>(false); // Ignore internal changes

    readonly id = signal<number>(0);
    readonly idString = computed(() => {
        const labelSuffix = this.labelId();
        return labelSuffix ? `s25ngCheckbox-${this.id()}-${labelSuffix}` : `s25ngCheckbox-${this.id()}`;
    });

    readonly cd = inject(ChangeDetectorRef);
    readonly elementRef = inject(ElementRef);

    static count: number = 0;
    constructor() {
        AngBridgeUtil.init(this);
        S25CheckboxComponent.count++;
        this.id.set(S25CheckboxComponent.count);
    }

    setValue(checked: boolean) {
        if (checked === this.value()) return;
        this.value.set(checked);
        this.onChange()?.({ data: this.value() });
    }

    showSuccess(msg: string = "Saved!") {
        // Clear first to ensure change detection triggers even if same message
        this.successMessage.set("");
        queueMicrotask(() => this.successMessage.set(msg));
    }

    select() {
        return this.setValue(true);
    }

    deselect() {
        return this.setValue(false);
    }

    isSelected(): boolean {
        return !!this.value();
    }

    passiveCheck(event: Event) {
        if (this.passive()) event.preventDefault();
    }
}
